import type { Session } from '@/libraries/tracking';
import type { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';

import { SnowplowSearchContext } from '@/helpers/tracking/providers';

import { Spectre } from '@/libraries/tracking';
import { useSessionManager } from '@/libraries/tracking/nuxt';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { isGoingBetweenShowAndSeason } from '@/features/tracking/helpers/route-helper';

import { useDebounceFn, createSharedComposable, tryOnScopeDispose } from '@vueuse/core';
import { onMounted } from 'vue';

const { defineSpec, createSpecTracker, SpecType } = Spectre<SnowplowContext>(() => TrackingHelper.trackEvent);

const specs: typeof SpecType = {
	/** Open search suggester */
	openSearch: {
		category: 'search',
		action: 'open',
	},

	/** Close search suggester */
	closeSearch: {
		category: 'search',
		action: 'close',
	},

	/** Submit a search term (enter) or open search results (show all). */
	submitSearch: defineSpec<{
		label: 'enter' | 'show_all';
	}>()({
		category: 'search',
		action: 'more',
	}),

	/** Click on title in recent titles, results dropdown, or search page results. */
	clickTitle: defineSpec<{
		label: 'recent' | 'suggestion' | 'results';
	}>()({
		category: 'userinteraction',
		action: 'title_clicked',
		property: 'search',
	}),

	/** When suggestions get refreshed and new title impressions are sent. */
	refreshSuggestions: {
		category: 'title',
		action: 'impression',
		label: 'suggestion',
		property: 'search',
	},

	/** Impression on the empty suggester state for recently visited search titles. */
	recentlyVisitedTitles: {
		category: 'title',
		action: 'impression',
		label: 'recent',
		property: 'search',
	},

	/** Click on suggested previous or popular search term. */
	clickSuggestedTerm: defineSpec<{
		action: 'recent_searched' | 'try_searching_for';
	}>()({
		category: 'search',
	}),

	clearPreviousResults: {
		category: 'search',
		action: 'clear_all',
	},

	clickSuggestedPerson: {
		category: 'search',
		action: 'person',
	},
};

const SearchSessionId = Symbol('SearchSession');
type SessionStart = { name: string; path: string };

function useSearchTrackingDef() {
	let previousSession: Session<SnowplowSearchContext> | null = null;

	/* SESSION */
	const { session, attachSession, killSession, startInterval, stopInterval } = useSessionManager<
		SnowplowSearchContext,
		SessionStart
	>({
		id: SearchSessionId,
		influenced: true,
		onSessionTimeout: prevSession => (previousSession = prevSession),
	});

	/* TRACKING */
	// as soon as search starts we also start a session so we can
	// use the context in there instead of a computed with the context
	const trackSearch = createSpecTracker({ specs, baseContexts: () => [session()?.data ?? null] });

	/* SESSION MANAGEMENT */
	function killSearchSession() {
		previousSession = killSession();
	}

	tryOnScopeDispose(() => stopInterval());

	const route = useRoute();
	onMounted(() => {
		if (route.name === 'app.search.list' && session() == null)
			startNewSession({ name: 'app.search.list', path: route.path });
	});

	// Start search session
	function startNewSession(start: SessionStart) {
		const prevSessionUuid = previousSession?.data.sessionUuid ?? session()?.data.sessionUuid ?? null;
		previousSession = null;

		startInterval();
		attachSession(start, new SnowplowSearchContext(crypto.randomUUID(), prevSessionUuid));
	}

	trackSearch.on('openSearch', () =>
		startNewSession({ name: route.name?.toString() ?? 'unknown', path: route.path })
	);

	// Update session data
	const updateSearchSession = useDebounceFn((newQuery: string) => {
		if (session() == null) startNewSession({ name: route.name?.toString() ?? 'unknown', path: route.path });

		const currentSession = session()!;
		const searchContext = currentSession.data;

		// We only want to track empty strings when the query was deleted *during* a search session.
		// New sessions always start with an empty query right, but we don't want to track those.
		if (!searchContext.searchEntries?.length && newQuery.trim() === '') return;

		searchContext.addSearchEntry(newQuery);

		currentSession.createdAt = Date.now();
	}, 500);

	// End search session
	const router = useRouter();
	router.beforeEach((to, from) => {
		if (to.name == null || from.name == null) {
			killSearchSession();
			return;
		}

		const currentSession = session();

		const startIsDetailPage = currentSession?.start.name?.startsWith('app.detail');
		const startIsSearchPage = currentSession?.start.name === 'app.search.list';

		// prettier-ignore
		const shouldKillSession =
			// there's a session to kill
			currentSession != null &&
			(
				// Not going to search page but also not a detail page, Popular for example should kill
				(to.name !== 'app.search.list' && !to.name.toString().startsWith('app.detail')) ||

				// from Search Page to non detail page
				(
					currentSession.start.name === 'app.search.list' &&
					from.name === 'app.search.list' &&
					!to.name?.toString().startsWith('app.detail')
				) ||

				// Session started on Detail page or Search Page and you go to another different title
				(
					!isGoingBetweenShowAndSeason(from, to) &&
					(startIsDetailPage || startIsSearchPage) &&
					to.name?.toString().startsWith('app.detail') &&
					from.name !== 'app.search.list' &&
					from.path !== currentSession.start.path
				)
			);

		if (shouldKillSession) killSearchSession();
	});

	return {
		trackSearch,
		killSearchSession,
		updateSearchSession,
	};
}

export const useSearchTracking = createSharedComposable(useSearchTrackingDef);
